import {Evaluator} from "@cm/graph-v3"
import {registerImageOpNode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/register-image-op-node"
import {ImageOpContextImgProc} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-imgproc"
import {createImageOpImgProc} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/imgproc/image-op-imgproc-utils"
import {ImageOpParametersOfType} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/specializing-image-op-node"
import {ImageOpInputsOrNodes} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {Definition} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/extract-region/definition"
import {determineDescriptor} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/extract-region/helpers"
import {ImageOpExtractRegionHal} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/extract-region/hal"
import {isBox2Like, wrap} from "@cm/math"
import {BasicSpecializingImageOpNode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/basic-specializing-image-op-node"

const nodeDefinition = {...Definition, nodeClass: "ImageOpNodeExtractRegion"}

@registerImageOpNode
export class ImageOpNodeExtractRegion extends BasicSpecializingImageOpNode<typeof Definition, ImageOpExtractRegionHal> {
    override get definition() {
        return nodeDefinition
    }

    constructor(parameters: ImageOpInputsOrNodes<typeof Definition>) {
        super(ImageOpExtractRegionHal, parameters)
    }

    override toImgProc = async (evaluator: Evaluator<ImageOpContextImgProc>, parameters: ImageOpParametersOfType<typeof Definition, "imgproc">) => {
        const evaluatedParameters = await evaluator.getAll(parameters)
        const descriptor = determineDescriptor(evaluatedParameters.sourceImage.descriptor, evaluatedParameters.region)
        if (!isBox2Like(evaluatedParameters.region)) {
            throw new Error("ImageOpNodeExtractRegion: Dynamic regions are not supported in ImgProc")
        }
        if (evaluatedParameters.mipLevelOffset !== 0) {
            throw new Error("ImageOpNodeExtractRegion: Mip level offset is not supported in ImgProc")
        }
        switch (evaluatedParameters.addressMode) {
            case "wrap": {
                const sx = wrap(evaluatedParameters.region.x, evaluatedParameters.sourceImage.descriptor.width)
                const sy = wrap(evaluatedParameters.region.y, evaluatedParameters.sourceImage.descriptor.height)
                const sw = Math.min(evaluatedParameters.region.width, evaluatedParameters.sourceImage.descriptor.width - sx)
                const sh = Math.min(evaluatedParameters.region.height, evaluatedParameters.sourceImage.descriptor.height - sy)
                if (sw <= 0 || sh <= 0) {
                    throw new Error("Invalid region")
                }
                let resultImage = evaluator.context.createEmptyImage(descriptor, {r: 0, g: 0, b: 0, a: 0})
                resultImage = createImageOpImgProc(descriptor, {
                    type: "copyRegion",
                    source: evaluatedParameters.sourceImage.node,
                    sourceRegion: {
                        type: "region",
                        region: [sx, sy, sw, sh],
                    },
                    target: resultImage.node,
                    targetOffset: {type: "offset", offset: [0, 0]},
                })
                if (sw < evaluatedParameters.region.width) {
                    resultImage = createImageOpImgProc(descriptor, {
                        type: "copyRegion",
                        source: evaluatedParameters.sourceImage.node,
                        sourceRegion: {
                            type: "region",
                            region: [0, sy, evaluatedParameters.region.width - sw, sh],
                        },
                        target: resultImage.node,
                        targetOffset: {type: "offset", offset: [sw, 0]},
                    })
                }
                if (sh < evaluatedParameters.region.height) {
                    resultImage = createImageOpImgProc(descriptor, {
                        type: "copyRegion",
                        source: evaluatedParameters.sourceImage.node,
                        sourceRegion: {
                            type: "region",
                            region: [sx, 0, sw, evaluatedParameters.region.height - sh],
                        },
                        target: resultImage.node,
                        targetOffset: {type: "offset", offset: [0, sh]},
                    })
                }
                if (sw < evaluatedParameters.region.width && sh < evaluatedParameters.region.height) {
                    resultImage = createImageOpImgProc(descriptor, {
                        type: "copyRegion",
                        source: evaluatedParameters.sourceImage.node,
                        sourceRegion: {
                            type: "region",
                            region: [0, 0, evaluatedParameters.region.width - sw, evaluatedParameters.region.height - sh],
                        },
                        target: resultImage.node,
                        targetOffset: {type: "offset", offset: [sw, sh]},
                    })
                }
                return resultImage
            }
            case "border": {
                const sx = Math.max(0, evaluatedParameters.region.x)
                const sy = Math.max(0, evaluatedParameters.region.y)
                const sw = Math.min(evaluatedParameters.region.width, evaluatedParameters.sourceImage.descriptor.width - sx)
                const sh = Math.min(evaluatedParameters.region.height, evaluatedParameters.sourceImage.descriptor.height - sy)
                const tx = Math.max(0, -evaluatedParameters.region.x)
                const ty = Math.max(0, -evaluatedParameters.region.y)
                let resultImage = evaluator.context.createEmptyImage(descriptor, {r: 0, g: 0, b: 0, a: 0})
                resultImage = createImageOpImgProc(descriptor, {
                    type: "copyRegion",
                    source: evaluatedParameters.sourceImage.node,
                    sourceRegion: {
                        type: "region",
                        region: [sx, sy, sw, sh],
                    },
                    target: resultImage.node,
                    targetOffset: {type: "offset", offset: [tx, ty]},
                })
                return resultImage
            }
            default:
                throw new Error("Unsupported address mode: " + evaluatedParameters.addressMode)
        }
    }
}
